import { useEffect, useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { ChartLineUp, Database, SignOut, Plus, Table, List, X, Gear } from "@phosphor-icons/react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sources, setSources] = useState([]);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/datasources");
      setSources(data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    load();
    const handler = () => load();
    window.addEventListener("datasources:changed", handler);
    return () => window.removeEventListener("datasources:changed", handler);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const navCls = ({ isActive }) =>
    `flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-medium transition-colors ${
      isActive
        ? "bg-accent text-primary"
        : "text-muted-foreground hover:text-foreground hover:bg-secondary"
    }`;

  const close = () => setOpen(false);

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-30 flex items-center justify-between px-4 h-14 bg-background/90 backdrop-blur border-b border-border">
        <div className="flex items-center gap-2">
          <ChartLineUp size={22} weight="bold" className="text-primary" />
          <span className="font-display font-extrabold text-base tracking-tight">Dashboard Power BI</span>
        </div>
        <button onClick={() => setOpen(true)} className="p-2 rounded-lg hover:bg-secondary" data-testid="open-sidebar">
          <List size={22} />
        </button>
      </div>

      {open && <div className="fixed inset-0 z-40 bg-black/30 lg:hidden" onClick={close} />}

      <aside className={`fixed top-0 left-0 h-screen w-64 z-50 flex flex-col bg-[hsl(120,32%,95%)] border-r border-border transition-transform duration-300 lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="p-5 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <ChartLineUp size={24} weight="bold" className="text-primary" />
            <span className="font-display font-extrabold text-lg tracking-tight">Dashboard Power BI</span>
          </div>
          <button onClick={close} className="lg:hidden p-1.5 rounded-lg hover:bg-secondary" data-testid="close-sidebar">
            <X size={18} />
          </button>
        </div>

        <div className="px-3">
          <NavLink to="/sources" className={navCls} onClick={close} data-testid="nav-sources">
            <Database size={18} /> Sumber Data
          </NavLink>
          <NavLink to="/settings" className={navCls} onClick={close} data-testid="nav-settings">
            <Gear size={18} /> Pengaturan
          </NavLink>
        </div>

        <div className="px-4 pt-4 pb-1.5 flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Dashboard</span>
          <NavLink to="/sources" className="text-muted-foreground hover:text-primary" onClick={close} data-testid="add-source-shortcut">
            <Plus size={15} />
          </NavLink>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 pb-4 space-y-1">
          {sources.length === 0 && (
            <p className="px-3 py-3 text-xs text-muted-foreground">Belum ada sumber data</p>
          )}
          {sources.map((s) => (
            <NavLink key={s.id} to={`/dashboard/${s.id}`} className={navCls} onClick={close} data-testid={`ds-nav-${s.id}`}>
              <Table size={16} className="shrink-0" />
              <span className="truncate">{s.name}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-border">
          <div className="px-2 py-2 mb-1">
            <p className="text-sm font-semibold truncate" data-testid="current-user-name">{user?.name}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          </div>
          <button onClick={handleLogout}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
            data-testid="logout-button">
            <SignOut size={18} /> Keluar
          </button>
        </div>
      </aside>

      <main className="lg:ml-64">
        <Outlet />
      </main>
    </div>
  );
}
